/**
 * DefencIQ Question Configuration
 * Complete questionnaire definitions with prompts, types, weights, criticality, and options
 * Kept separate from UI components for maintainability
 */

export const QUESTION_CONFIG = {
  // ENGAGEMENT SECTION (not scored)
  engagement: {
    section_id: "engagement",
    questions: [
      {
        question_id: "ENG-01",
        field_id: "assessment_id",
        prompt: "Assessment ID",
        type: "text",
        weight: 0,
        criticality: null,
        gating_rule: false,
        required: true
      },
      {
        question_id: "ENG-02",
        field_id: "client_name",
        prompt: "Client Name",
        type: "text",
        autocomplete_source: "clients",
        weight: 0,
        criticality: null,
        gating_rule: false,
        required: true
      },
      {
        question_id: "ENG-03",
        field_id: "assessor_name",
        prompt: "Assessor Name",
        type: "text",
        weight: 0,
        criticality: null,
        gating_rule: false,
        required: true
      },
      {
        question_id: "ENG-04",
        field_id: "assessment_date",
        prompt: "Assessment Date",
        type: "date",
        weight: 0,
        criticality: null,
        gating_rule: false,
        required: true
      },
      {
        question_id: "ENG-05",
        field_id: "engagement_type",
        prompt: "Engagement Type",
        type: "select",
        weight: 0,
        criticality: null,
        gating_rule: false,
        required: true,
        options: [
          { value: "discovery_only", label: "Discovery Only" },
          { value: "snapshot_assessment", label: "Snapshot Assessment" },
          { value: "managed_compliance_onboarding", label: "Managed Compliance Onboarding" },
          { value: "greenfield_ml2_build", label: "Greenfield ML2 Build" },
          { value: "disp_readiness_uplift", label: "DISP Readiness Uplift" }
        ]
      },
      {
        question_id: "ENG-06",
        field_id: "notes",
        prompt: "Engagement Notes",
        type: "textarea",
        weight: 0,
        criticality: null,
        gating_rule: false,
        required: false
      }
    ]
  },

  // ORGANISATION PROFILE SECTION
  organisation_profile: {
    section_id: "organisation_profile",
    questions: [
      {
        question_id: "ORG-01",
        field_id: "industry_sector",
        prompt: "What industry sector does the organisation operate in?",
        type: "select",
        weight: 1,
        criticality: "low",
        gating_rule: false,
        required: true,
        recommended_components: ["disp_dashboard"],
        options: [
          { value: "defence_primes", label: "Defence Primes" },
          { value: "defence_sme_engineering", label: "Defence SME (Engineering)" },
          { value: "defence_sme_it_cyber", label: "Defence SME (IT/Cyber)" },
          { value: "defence_supply_chain_manufacturing", label: "Defence Supply Chain (Manufacturing)" },
          { value: "aerospace", label: "Aerospace" },
          { value: "maritime", label: "Maritime" },
          { value: "government_federal", label: "Government (Federal)" },
          { value: "government_state_local", label: "Government (State/Local)" },
          { value: "critical_infrastructure", label: "Critical Infrastructure" },
          { value: "professional_services", label: "Professional Services" },
          { value: "technology_saas", label: "Technology / SaaS" },
          { value: "other", label: "Other" }
        ]
      },
      {
        question_id: "ORG-01a",
        field_id: "industry_sector_other",
        prompt: "If 'Other', please specify the industry sector",
        type: "text",
        weight: 0,
        criticality: null,
        gating_rule: false,
        required: false,
        conditionalOn: { field: "industry_sector", value: "other" }
      },
      {
        question_id: "ORG-02",
        field_id: "disp_status",
        prompt: "What is the organisation's current DISP membership status?",
        type: "selectScored",
        weight: 2,
        criticality: "medium",
        gating_rule: false,
        required: true,
        recommended_components: ["disp_dashboard"],
        options: [
          { value: "current_member", label: "Current DISP Member", score: 2 },
          { value: "applying_within_3_months", label: "Applying within 3 months", score: 2 },
          { value: "applying_within_12_months", label: "Applying within 12 months", score: 1 },
          { value: "considering_disp", label: "Considering DISP", score: 1 },
          { value: "unknown", label: "Unknown", score: 0 }
        ]
      },
      {
        question_id: "ORG-03",
        field_id: "defence_opportunities",
        prompt: "What Defence contracting opportunities is the organisation targeting?",
        type: "multiselect",
        weight: 2,
        criticality: "medium",
        gating_rule: false,
        required: true,
        recommended_components: ["disp_dashboard"],
        notesPlaceholder: "Additional context on Defence opportunities (optional)",
        options: [
          { value: "existing_direct_contract", label: "Existing contract with Defence (direct)" },
          { value: "existing_subcontract", label: "Existing subcontract to Defence Prime" },
          { value: "bidding_direct", label: "Bidding for Defence contract (direct)" },
          { value: "bidding_subcontractor", label: "Bidding as subcontractor to Prime" },
          { value: "engaged_prime_precontract", label: "Engaged with Defence Prime (pre-contract)" },
          { value: "working_towards_disp", label: "Working towards DISP membership" },
          { value: "responding_rft_rfp", label: "Responding to RFT / RFP" },
          { value: "on_supplier_panel", label: "On Defence supplier panel / register" },
          { value: "planning_to_enter", label: "Not currently engaged but planning to enter Defence" },
          { value: "unknown", label: "Unknown" }
        ]
      },
      {
        question_id: "ORG-04",
        field_id: "user_count_tier",
        prompt: "How many users does the organisation have?",
        type: "select",
        weight: 1,
        criticality: "low",
        gating_rule: false,
        required: true,
        recommended_components: ["microsoft_entra_id"],
        notesPlaceholder: "Exact user count (optional)",
        options: [
          { value: "1-10", label: "1–10" },
          { value: "11-25", label: "11–25" },
          { value: "26-50", label: "26–50" },
          { value: "51-100", label: "51–100" },
          { value: "101-250", label: "101–250" },
          { value: "251-500", label: "251–500" },
          { value: "500+", label: "500+" }
        ]
      },
      {
        question_id: "ORG-05",
        field_id: "endpoint_count",
        prompt: "How many endpoints (computers/devices) does the organisation manage?",
        type: "number",
        weight: 1,
        criticality: "low",
        gating_rule: false,
        required: true,
        recommended_components: ["microsoft_intune"]
      },
      {
        question_id: "ORG-06",
        field_id: "it_support_model",
        prompt: "What is the current IT support model?",
        type: "selectScored",
        weight: 2,
        criticality: "medium",
        gating_rule: false,
        required: true,
        recommended_components: ["microsoft_intune"],
        options: [
          { value: "internal_it", label: "Internal IT Team", score: 2 },
          { value: "msp", label: "Managed Service Provider (MSP)", score: 2 },
          { value: "hybrid", label: "Hybrid (Internal + MSP)", score: 2 },
          { value: "ad_hoc_support", label: "Ad-hoc Support", score: 1 },
          { value: "none", label: "None", score: 0 }
        ]
      },
      {
        question_id: "ORG-07",
        field_id: "core_platforms",
        prompt: "What core platforms does the organisation use?",
        type: "multiselect",
        weight: 2,
        criticality: "medium",
        gating_rule: false,
        required: true,
        recommended_components: ["microsoft_entra_id", "microsoft_intune"],
        options: [
          { value: "microsoft_365", label: "Microsoft 365" },
          { value: "google_workspace", label: "Google Workspace" },
          { value: "aws", label: "AWS" },
          { value: "azure", label: "Azure" },
          { value: "on_prem_active_directory", label: "On-premises Active Directory" },
          { value: "linux_servers", label: "Linux Servers" },
          { value: "windows_servers", label: "Windows Servers" },
          { value: "other", label: "Other" }
        ]
      },
      {
        question_id: "ORG-08",
        field_id: "corporate_managed_devices",
        prompt: "Are corporate-managed devices used for work?",
        type: "status",
        weight: 2,
        criticality: "medium",
        gating_rule: false,
        required: true,
        recommended_components: ["microsoft_intune"]
      },
      {
        question_id: "ORG-09",
        field_id: "disp_08_hosting_model",
        prompt: "How are your systems primarily hosted?",
        type: "selectScored",
        weight: 2,
        criticality: "medium",
        gating_rule: false,
        required: true,
        options: [
          { value: "cloud", label: "Cloud", score: 2 },
          { value: "on_prem", label: "On-premises", score: 1 },
          { value: "hybrid", label: "Hybrid", score: 2 },
          { value: "unsure", label: "Unsure", score: 0 }
        ]
      }
    ]
  },

  // CURRENT TOOLING & PLATFORMS SECTION (unscored)
  current_tooling: {
    section_id: "current_tooling",
    questions: [
      {
        question_id: "TOL-01",
        field_id: "stack_microsoft_entra_id_status",
        prompt: "What is the current status of Microsoft Entra ID or equivalent cloud identity platform?",
        type: "select",
        weight: 0,
        criticality: null,
        gating_rule: false,
        required: true,
        notesPlaceholder: "Implementation notes for identity platform (optional)",
        options: [
          { value: "implemented_effective", label: "Implemented and operationally effective" },
          { value: "implemented_partial", label: "Implemented but only partially deployed / partially relied upon" },
          { value: "implemented_limited", label: "Present in environment but not meaningfully configured or relied upon" },
          { value: "planned_not_implemented", label: "Planned / licensed / procured but not yet implemented" },
          { value: "not_present", label: "Not present" },
          { value: "unknown", label: "Unknown / not confirmed" }
        ]
      },
      {
        question_id: "TOL-02",
        field_id: "stack_conditional_access_status",
        prompt: "What is the current status of Conditional Access or equivalent context-aware access policy capability?",
        type: "select",
        weight: 0,
        criticality: null,
        gating_rule: false,
        required: true,
        notesPlaceholder: "Implementation notes for conditional access (optional)",
        options: [
          { value: "implemented_effective", label: "Implemented and operationally effective" },
          { value: "implemented_partial", label: "Implemented but only partially deployed / partially relied upon" },
          { value: "implemented_limited", label: "Present in environment but not meaningfully configured or relied upon" },
          { value: "planned_not_implemented", label: "Planned / licensed / procured but not yet implemented" },
          { value: "not_present", label: "Not present" },
          { value: "unknown", label: "Unknown / not confirmed" }
        ]
      },
      {
        question_id: "TOL-03",
        field_id: "stack_microsoft_intune_status",
        prompt: "What is the current status of Microsoft Intune or equivalent endpoint/device management platform?",
        type: "select",
        weight: 0,
        criticality: null,
        gating_rule: false,
        required: true,
        notesPlaceholder: "Implementation notes for endpoint management platform (optional)",
        options: [
          { value: "implemented_effective", label: "Implemented and operationally effective" },
          { value: "implemented_partial", label: "Implemented but only partially deployed / partially relied upon" },
          { value: "implemented_limited", label: "Present in environment but not meaningfully configured or relied upon" },
          { value: "planned_not_implemented", label: "Planned / licensed / procured but not yet implemented" },
          { value: "not_present", label: "Not present" },
          { value: "unknown", label: "Unknown / not confirmed" }
        ]
      },
      {
        question_id: "TOL-04",
        field_id: "stack_microsoft_defender_for_endpoint_status",
        prompt: "What is the current status of Microsoft Defender for Endpoint or equivalent EDR platform?",
        type: "select",
        weight: 0,
        criticality: null,
        gating_rule: false,
        required: true,
        notesPlaceholder: "Implementation notes for EDR platform (optional)",
        options: [
          { value: "implemented_effective", label: "Implemented and operationally effective" },
          { value: "implemented_partial", label: "Implemented but only partially deployed / partially relied upon" },
          { value: "implemented_limited", label: "Present in environment but not meaningfully configured or relied upon" },
          { value: "planned_not_implemented", label: "Planned / licensed / procured but not yet implemented" },
          { value: "not_present", label: "Not present" },
          { value: "unknown", label: "Unknown / not confirmed" }
        ]
      },
      {
        question_id: "TOL-05",
        field_id: "stack_wdac_or_applocker_status",
        prompt: "What is the current status of WDAC / AppLocker or equivalent application allow-listing capability?",
        type: "select",
        weight: 0,
        criticality: null,
        gating_rule: false,
        required: true,
        notesPlaceholder: "Implementation notes for application control (optional)",
        options: [
          { value: "implemented_effective", label: "Implemented and operationally effective" },
          { value: "implemented_partial", label: "Implemented but only partially deployed / partially relied upon" },
          { value: "implemented_limited", label: "Present in environment but not meaningfully configured or relied upon" },
          { value: "planned_not_implemented", label: "Planned / licensed / procured but not yet implemented" },
          { value: "not_present", label: "Not present" },
          { value: "unknown", label: "Unknown / not confirmed" }
        ]
      },
      {
        question_id: "TOL-06",
        field_id: "stack_tenable_status",
        prompt: "What is the current status of Tenable?",
        type: "select",
        weight: 0,
        criticality: null,
        gating_rule: false,
        required: true,
        notesPlaceholder: "Implementation notes for Tenable (optional)",
        options: [
          { value: "implemented_effective", label: "Implemented and operationally effective" },
          { value: "implemented_partial", label: "Implemented but only partially deployed / partially relied upon" },
          { value: "implemented_limited", label: "Present in environment but not meaningfully configured or relied upon" },
          { value: "planned_not_implemented", label: "Planned / licensed / procured but not yet implemented" },
          { value: "not_present", label: "Not present" },
          { value: "unknown", label: "Unknown / not confirmed" }
        ]
      },
      {
        question_id: "TOL-07",
        field_id: "stack_qualys_status",
        prompt: "What is the current status of Qualys?",
        type: "select",
        weight: 0,
        criticality: null,
        gating_rule: false,
        required: true,
        notesPlaceholder: "Implementation notes for Qualys (optional)",
        options: [
          { value: "implemented_effective", label: "Implemented and operationally effective" },
          { value: "implemented_partial", label: "Implemented but only partially deployed / partially relied upon" },
          { value: "implemented_limited", label: "Present in environment but not meaningfully configured or relied upon" },
          { value: "planned_not_implemented", label: "Planned / licensed / procured but not yet implemented" },
          { value: "not_present", label: "Not present" },
          { value: "unknown", label: "Unknown / not confirmed" }
        ]
      },
      {
        question_id: "TOL-08",
        field_id: "stack_microsoft_sentinel_status",
        prompt: "What is the current status of Microsoft Sentinel or equivalent SIEM platform?",
        type: "select",
        weight: 0,
        criticality: null,
        gating_rule: false,
        required: true,
        notesPlaceholder: "Implementation notes for SIEM platform (optional)",
        options: [
          { value: "implemented_effective", label: "Implemented and operationally effective" },
          { value: "implemented_partial", label: "Implemented but only partially deployed / partially relied upon" },
          { value: "implemented_limited", label: "Present in environment but not meaningfully configured or relied upon" },
          { value: "planned_not_implemented", label: "Planned / licensed / procured but not yet implemented" },
          { value: "not_present", label: "Not present" },
          { value: "unknown", label: "Unknown / not confirmed" }
        ]
      },
      {
        question_id: "TOL-09",
        field_id: "stack_privileged_access_management_status",
        prompt: "What is the current status of Privileged Access Management?",
        type: "select",
        weight: 0,
        criticality: null,
        gating_rule: false,
        required: true,
        notesPlaceholder: "Implementation notes for PAM (optional)",
        options: [
          { value: "implemented_effective", label: "Implemented and operationally effective" },
          { value: "implemented_partial", label: "Implemented but only partially deployed / partially relied upon" },
          { value: "implemented_limited", label: "Present in environment but not meaningfully configured or relied upon" },
          { value: "planned_not_implemented", label: "Planned / licensed / procured but not yet implemented" },
          { value: "not_present", label: "Not present" },
          { value: "unknown", label: "Unknown / not confirmed" }
        ]
      },
      {
        question_id: "TOL-10",
        field_id: "stack_backup_platform_status",
        prompt: "What is the current status of the organisation's backup platform?",
        type: "select",
        weight: 0,
        criticality: null,
        gating_rule: false,
        required: true,
        notesPlaceholder: "Implementation notes for backup platform (optional)",
        options: [
          { value: "implemented_effective", label: "Implemented and operationally effective" },
          { value: "implemented_partial", label: "Implemented but only partially deployed / partially relied upon" },
          { value: "implemented_limited", label: "Present in environment but not meaningfully configured or relied upon" },
          { value: "planned_not_implemented", label: "Planned / licensed / procured but not yet implemented" },
          { value: "not_present", label: "Not present" },
          { value: "unknown", label: "Unknown / not confirmed" }
        ]
      },
      {
        question_id: "TOL-11",
        field_id: "stack_drata_status",
        prompt: "What is the current status of Drata or equivalent compliance automation platform?",
        type: "select",
        weight: 0,
        criticality: null,
        gating_rule: false,
        required: true,
        notesPlaceholder: "Implementation notes for compliance automation (optional)",
        options: [
          { value: "implemented_effective", label: "Implemented and operationally effective" },
          { value: "implemented_partial", label: "Implemented but only partially deployed / partially relied upon" },
          { value: "implemented_limited", label: "Present in environment but not meaningfully configured or relied upon" },
          { value: "planned_not_implemented", label: "Planned / licensed / procured but not yet implemented" },
          { value: "not_present", label: "Not present" },
          { value: "unknown", label: "Unknown / not confirmed" }
        ]
      },
      {
        question_id: "TOL-12",
        field_id: "stack_remediation_tracker_status",
        prompt: "What is the current status of a remediation tracking platform or structured remediation workflow?",
        type: "select",
        weight: 0,
        criticality: null,
        gating_rule: false,
        required: true,
        notesPlaceholder: "Implementation notes for remediation tracking (optional)",
        options: [
          { value: "implemented_effective", label: "Implemented and operationally effective" },
          { value: "implemented_partial", label: "Implemented but only partially deployed / partially relied upon" },
          { value: "implemented_limited", label: "Present in environment but not meaningfully configured or relied upon" },
          { value: "planned_not_implemented", label: "Planned / licensed / procured but not yet implemented" },
          { value: "not_present", label: "Not present" },
          { value: "unknown", label: "Unknown / not confirmed" }
        ]
      },
      {
        question_id: "TOL-13",
        field_id: "stack_evidence_pack_generator_status",
        prompt: "What is the current status of an evidence pack generation capability?",
        type: "select",
        weight: 0,
        criticality: null,
        gating_rule: false,
        required: true,
        notesPlaceholder: "Implementation notes for evidence generation (optional)",
        options: [
          { value: "implemented_effective", label: "Implemented and operationally effective" },
          { value: "implemented_partial", label: "Implemented but only partially deployed / partially relied upon" },
          { value: "implemented_limited", label: "Present in environment but not meaningfully configured or relied upon" },
          { value: "planned_not_implemented", label: "Planned / licensed / procured but not yet implemented" },
          { value: "not_present", label: "Not present" },
          { value: "unknown", label: "Unknown / not confirmed" }
        ]
      },
      {
        question_id: "TOL-14",
        field_id: "stack_disp_dashboard_status",
        prompt: "What is the current status of a DISP dashboard / CSQ tracking capability?",
        type: "select",
        weight: 0,
        criticality: null,
        gating_rule: false,
        required: true,
        notesPlaceholder: "Implementation notes for DISP dashboard (optional)",
        options: [
          { value: "implemented_effective", label: "Implemented and operationally effective" },
          { value: "implemented_partial", label: "Implemented but only partially deployed / partially relied upon" },
          { value: "implemented_limited", label: "Present in environment but not meaningfully configured or relied upon" },
          { value: "planned_not_implemented", label: "Planned / licensed / procured but not yet implemented" },
          { value: "not_present", label: "Not present" },
          { value: "unknown", label: "Unknown / not confirmed" }
        ]
      },
      {
        question_id: "TOL-15",
        field_id: "stack_primary_tooling_model",
        prompt: "How would you describe the organisation's overall security tooling posture?",
        type: "select",
        weight: 0,
        criticality: null,
        gating_rule: false,
        required: true,
        options: [
          { value: "integrated_and_centralised", label: "Integrated and centralised" },
          { value: "mostly_centralised", label: "Mostly centralised with some gaps" },
          { value: "mixed_fragmented", label: "Mixed / fragmented across multiple tools" },
          { value: "largely_manual", label: "Largely manual or loosely managed" },
          { value: "unknown", label: "Unknown" }
        ]
      }
    ]
  },

  // IDENTITY AND MFA SECTION
  identity_and_mfa: {
    section_id: "identity_and_mfa",
    questions: [
      {
        question_id: "IDM-01",
        field_id: "identity_platform",
        prompt: "What identity platform does the organisation use?",
        type: "selectScored",
        weight: 3,
        criticality: "high",
        gating_rule: false,
        required: true,
        recommended_components: ["microsoft_entra_id", "conditional_access"],
        options: [
          { value: "microsoft_entra_id", label: "Microsoft Entra ID", score: 2 },
          { value: "on_prem_active_directory", label: "On-premises Active Directory", score: 1 },
          { value: "google_identity", label: "Google Identity", score: 1 },
          { value: "okta", label: "Okta", score: 2 },
          { value: "other", label: "Other", score: 1 },
          { value: "unknown", label: "Unknown", score: 0 }
        ]
      },
      {
        question_id: "IDM-02",
        field_id: "mfa_all_users",
        prompt: "Is MFA enforced for all users?",
        type: "status",
        weight: 5,
        criticality: "critical",
        gating_rule: true,
        required: true,
        recommended_components: ["microsoft_entra_id", "conditional_access"],
        recommended_actions: ["Implement MFA for all users using Microsoft Entra ID or equivalent"]
      },
      {
        question_id: "IDM-03",
        field_id: "mfa_privileged_users",
        prompt: "Is MFA enforced for all privileged users?",
        type: "status",
        weight: 5,
        criticality: "critical",
        gating_rule: true,
        required: true,
        recommended_components: ["microsoft_entra_id", "conditional_access", "privileged_access_management"],
        recommended_actions: ["Enforce MFA for all privileged accounts immediately"]
      },
      {
        question_id: "IDM-04",
        field_id: "mfa_methods",
        prompt: "What MFA methods are in use?",
        type: "multiselect",
        weight: 2,
        criticality: "medium",
        gating_rule: false,
        required: true,
        recommended_components: ["microsoft_entra_id"],
        options: [
          { value: "authenticator_app", label: "Authenticator App", score: 2 },
          { value: "hardware_token", label: "Hardware Token", score: 2 },
          { value: "fido2", label: "FIDO2 Security Key", score: 2 },
          { value: "sms", label: "SMS", score: 1 },
          { value: "voice_call", label: "Voice Call", score: 1 },
          { value: "none", label: "None", score: 0 },
          { value: "unknown", label: "Unknown", score: 0 }
        ]
      },
      {
        question_id: "IDM-05",
        field_id: "conditional_access",
        prompt: "Are conditional access policies configured?",
        type: "status",
        weight: 3,
        criticality: "high",
        gating_rule: false,
        required: true,
        recommended_components: ["conditional_access"],
        recommended_actions: ["Configure conditional access policies to control access based on device compliance and location"]
      },
      {
        question_id: "IDM-06",
        field_id: "shared_or_generic_accounts",
        prompt: "Are shared or generic accounts used?",
        type: "statusReverse",
        weight: 2,
        criticality: "medium",
        gating_rule: false,
        required: true,
        recommended_components: ["microsoft_entra_id"],
        recommended_actions: ["Eliminate shared accounts and implement individual user accounts"]
      }
    ]
  },

  // ENDPOINT MANAGEMENT SECTION
  endpoint_management: {
    section_id: "endpoint_management",
    questions: [
      {
        question_id: "EPM-01",
        field_id: "central_device_management",
        prompt: "What central device management solution is in place?",
        type: "selectScored",
        weight: 4,
        criticality: "critical",
        gating_rule: true,
        required: true,
        recommended_components: ["microsoft_intune"],
        options: [
          { value: "intune", label: "Microsoft Intune", score: 2 },
          { value: "sccm", label: "SCCM/ConfigMgr", score: 2 },
          { value: "other_mdm", label: "Other MDM Solution", score: 1 },
          { value: "none", label: "None", score: 0 },
          { value: "unknown", label: "Unknown", score: 0 }
        ]
      },
      {
        question_id: "EPM-02",
        field_id: "device_compliance_policies",
        prompt: "Are device compliance policies defined and enforced?",
        type: "status",
        weight: 4,
        criticality: "critical",
        gating_rule: true,
        required: true,
        recommended_components: ["microsoft_intune", "conditional_access"],
        recommended_actions: ["Implement device compliance policies including encryption, PIN, and OS version requirements"]
      },
      {
        question_id: "EPM-03",
        field_id: "corporate_devices_required",
        prompt: "Are corporate-managed devices required for work?",
        type: "status",
        weight: 2,
        criticality: "medium",
        gating_rule: false,
        required: true,
        recommended_components: ["microsoft_intune"]
      },
      {
        question_id: "EPM-04",
        field_id: "byod_allowed",
        prompt: "Is BYOD (Bring Your Own Device) allowed?",
        type: "statusReverse",
        weight: 2,
        criticality: "medium",
        gating_rule: false,
        required: true,
        recommended_components: ["microsoft_intune", "conditional_access"],
        recommended_actions: ["If BYOD is allowed, ensure proper MAM policies are in place"]
      },
      {
        question_id: "EPM-05",
        field_id: "remote_config_or_wipe",
        prompt: "Can devices be remotely configured or wiped?",
        type: "status",
        weight: 3,
        criticality: "high",
        gating_rule: false,
        required: true,
        recommended_components: ["microsoft_intune"],
        recommended_actions: ["Enable remote wipe and configuration capabilities through MDM"]
      }
    ]
  },

  // APPLICATION CONTROL SECTION
  application_control: {
    section_id: "application_control",
    questions: [
      {
        question_id: "APP-01",
        field_id: "restrict_which_apps_can_run",
        prompt: "Are restrictions in place to control which applications can run?",
        type: "status",
        weight: 5,
        criticality: "critical",
        gating_rule: true,
        required: true,
        recommended_components: ["microsoft_defender_for_endpoint", "wdac_or_applocker"],
        recommended_actions: ["Implement application control using WDAC or AppLocker to restrict executable files"]
      },
      {
        question_id: "APP-02",
        field_id: "allow_listing_in_place",
        prompt: "Is application allow-listing (whitelisting) implemented?",
        type: "status",
        weight: 5,
        criticality: "critical",
        gating_rule: true,
        required: true,
        recommended_components: ["wdac_or_applocker", "microsoft_defender_for_endpoint"],
        recommended_actions: ["Deploy application allow-listing to permit only approved applications"]
      },
      {
        question_id: "APP-03",
        field_id: "users_can_install_software",
        prompt: "Can standard users install software without IT approval?",
        type: "statusReverse",
        weight: 3,
        criticality: "high",
        gating_rule: false,
        required: true,
        recommended_components: ["microsoft_intune", "wdac_or_applocker"],
        recommended_actions: ["Remove local admin rights and implement software deployment through IT"]
      },
      {
        question_id: "APP-04",
        field_id: "application_execution_monitored",
        prompt: "Is application execution monitored and logged?",
        type: "status",
        weight: 3,
        criticality: "high",
        gating_rule: false,
        required: true,
        recommended_components: ["microsoft_defender_for_endpoint", "microsoft_sentinel"],
        recommended_actions: ["Enable application execution logging and forward to SIEM"]
      }
    ]
  },

  // PATCH MANAGEMENT SECTION
  patch_management: {
    section_id: "patch_management",
    questions: [
      {
        question_id: "PAT-01",
        field_id: "os_patch_management_method",
        prompt: "What method is used for OS patch management?",
        type: "selectScored",
        weight: 3,
        criticality: "high",
        gating_rule: false,
        required: true,
        recommended_components: ["microsoft_intune"],
        options: [
          { value: "intune", label: "Microsoft Intune", score: 2 },
          { value: "wsus", label: "WSUS", score: 2 },
          { value: "msp_managed", label: "MSP Managed", score: 2 },
          { value: "manual", label: "Manual", score: 1 },
          { value: "other", label: "Other", score: 1 },
          { value: "unknown", label: "Unknown", score: 0 }
        ]
      },
      {
        question_id: "PAT-02",
        field_id: "critical_patch_sla",
        prompt: "What is the SLA for applying critical security patches?",
        type: "selectScored",
        weight: 5,
        criticality: "critical",
        gating_rule: true,
        required: true,
        recommended_components: ["microsoft_intune", "tenable", "qualys"],
        recommended_actions: ["Establish and enforce a 48-hour SLA for critical patches"],
        options: [
          { value: "within_48_hours", label: "Within 48 hours", score: 2 },
          { value: "within_7_days", label: "Within 7 days", score: 1 },
          { value: "within_30_days", label: "Within 30 days", score: 1 },
          { value: "greater_than_30_days", label: "Greater than 30 days", score: 0 },
          { value: "not_defined", label: "Not defined", score: 0 },
          { value: "unknown", label: "Unknown", score: 0 }
        ]
      },
      {
        question_id: "PAT-03",
        field_id: "central_patch_compliance_tracking",
        prompt: "Is there central tracking of patch compliance?",
        type: "status",
        weight: 4,
        criticality: "critical",
        gating_rule: true,
        required: true,
        recommended_components: ["microsoft_intune", "tenable", "qualys"],
        recommended_actions: ["Implement centralised patch compliance reporting and monitoring"]
      },
      {
        question_id: "PAT-04",
        field_id: "vulnerability_scanning_used",
        prompt: "Is vulnerability scanning performed regularly?",
        type: "status",
        weight: 3,
        criticality: "high",
        gating_rule: false,
        required: true,
        recommended_components: ["tenable", "qualys"],
        recommended_actions: ["Implement regular vulnerability scanning with automated reporting"]
      },
      {
        question_id: "PAT-05",
        field_id: "real_time_identification_of_unpatched_systems",
        prompt: "Can unpatched systems be identified in real-time?",
        type: "status",
        weight: 3,
        criticality: "high",
        gating_rule: false,
        required: true,
        recommended_components: ["tenable", "qualys", "microsoft_intune"],
        recommended_actions: ["Deploy continuous vulnerability assessment and reporting"]
      }
    ]
  },

  // PRIVILEGED ACCESS SECTION
  privileged_access: {
    section_id: "privileged_access",
    questions: [
      {
        question_id: "PRA-01",
        field_id: "local_admin_rights_widespread",
        prompt: "Do standard users have local administrator rights?",
        type: "statusReverse",
        weight: 4,
        criticality: "high",
        gating_rule: false,
        required: true,
        recommended_components: ["microsoft_intune", "privileged_access_management"],
        recommended_actions: ["Remove local admin rights from standard user accounts"]
      },
      {
        question_id: "PRA-02",
        field_id: "separate_admin_accounts",
        prompt: "Are separate admin accounts used for privileged tasks?",
        type: "status",
        weight: 4,
        criticality: "critical",
        gating_rule: true,
        required: true,
        recommended_components: ["microsoft_entra_id", "privileged_access_management"],
        recommended_actions: ["Implement separate privileged accounts with naming convention"]
      },
      {
        question_id: "PRA-03",
        field_id: "admin_activity_logged_and_reviewed",
        prompt: "Is admin activity logged and regularly reviewed?",
        type: "status",
        weight: 4,
        criticality: "critical",
        gating_rule: true,
        required: true,
        recommended_components: ["microsoft_sentinel", "privileged_access_management"],
        recommended_actions: ["Enable logging for all privileged operations and implement regular reviews"]
      },
      {
        question_id: "PRA-04",
        field_id: "temporary_privilege_elevation",
        prompt: "Is just-in-time or temporary privilege elevation available?",
        type: "status",
        weight: 3,
        criticality: "high",
        gating_rule: false,
        required: true,
        recommended_components: ["privileged_access_management", "microsoft_entra_id"],
        recommended_actions: ["Implement PIM (Privileged Identity Management) for just-in-time access"]
      }
    ]
  },

  // OFFICE MACROS AND USER HARDENING SECTION
  office_macros_and_user_hardening: {
    section_id: "office_macros_and_user_hardening",
    questions: [
      {
        question_id: "HAR-01",
        field_id: "internet_macros_blocked",
        prompt: "Are macros from the internet blocked?",
        type: "status",
        weight: 4,
        criticality: "critical",
        gating_rule: true,
        required: true,
        recommended_components: ["microsoft_intune", "microsoft_defender_for_endpoint"],
        recommended_actions: ["Block macros in files from the internet via Group Policy or Intune"]
      },
      {
        question_id: "HAR-02",
        field_id: "trusted_macro_locations_only",
        prompt: "Are macros only allowed from trusted locations?",
        type: "status",
        weight: 3,
        criticality: "high",
        gating_rule: false,
        required: true,
        recommended_components: ["microsoft_intune"],
        recommended_actions: ["Configure trusted locations for macro-enabled documents"]
      },
      {
        question_id: "HAR-03",
        field_id: "browser_security_settings_enforced",
        prompt: "Are browser security settings centrally enforced?",
        type: "status",
        weight: 3,
        criticality: "high",
        gating_rule: false,
        required: true,
        recommended_components: ["microsoft_intune", "microsoft_defender_for_endpoint"],
        recommended_actions: ["Deploy browser security policies via Intune or GPO"]
      },
      {
        question_id: "HAR-04",
        field_id: "legacy_plugins_disabled",
        prompt: "Are legacy browser plugins (Flash, Java) disabled?",
        type: "status",
        weight: 2,
        criticality: "medium",
        gating_rule: false,
        required: true,
        recommended_components: ["microsoft_intune"],
        recommended_actions: ["Ensure legacy plugins are blocked across all browsers"]
      }
    ]
  },

  // BACKUPS SECTION
  backups: {
    section_id: "backups",
    questions: [
      {
        question_id: "BKP-01",
        field_id: "systems_backed_up",
        prompt: "What systems are currently backed up?",
        type: "multiselect",
        weight: 3,
        criticality: "high",
        gating_rule: false,
        required: true,
        recommended_components: ["backup_platform"],
        options: [
          { value: "endpoints", label: "Endpoints" },
          { value: "servers", label: "Servers" },
          { value: "microsoft_365", label: "Microsoft 365" },
          { value: "file_shares", label: "File Shares" },
          { value: "line_of_business_apps", label: "Line of Business Apps" },
          { value: "cloud_workloads", label: "Cloud Workloads" },
          { value: "other", label: "Other" }
        ]
      },
      {
        question_id: "BKP-02",
        field_id: "backup_frequency",
        prompt: "How frequently are backups performed?",
        type: "selectScored",
        weight: 3,
        criticality: "high",
        gating_rule: false,
        required: true,
        recommended_components: ["backup_platform"],
        options: [
          { value: "continuous", label: "Continuous", score: 2 },
          { value: "daily", label: "Daily", score: 2 },
          { value: "weekly", label: "Weekly", score: 1 },
          { value: "monthly", label: "Monthly", score: 0 },
          { value: "ad_hoc", label: "Ad-hoc", score: 0 },
          { value: "unknown", label: "Unknown", score: 0 }
        ]
      },
      {
        question_id: "BKP-03",
        field_id: "offline_or_immutable_backups",
        prompt: "Are offline or immutable backups maintained?",
        type: "status",
        weight: 5,
        criticality: "critical",
        gating_rule: true,
        required: true,
        recommended_components: ["backup_platform"],
        recommended_actions: ["Implement immutable or air-gapped backup copies"]
      },
      {
        question_id: "BKP-04",
        field_id: "last_successful_restore_test",
        prompt: "When was the last successful backup restore test?",
        type: "selectScored",
        weight: 4,
        criticality: "critical",
        gating_rule: true,
        required: true,
        recommended_components: ["backup_platform"],
        recommended_actions: ["Perform regular backup restore testing at least quarterly"],
        options: [
          { value: "within_30_days", label: "Within 30 days", score: 2 },
          { value: "within_90_days", label: "Within 90 days", score: 2 },
          { value: "within_12_months", label: "Within 12 months", score: 1 },
          { value: "more_than_12_months", label: "More than 12 months ago", score: 0 },
          { value: "never", label: "Never", score: 0 },
          { value: "unknown", label: "Unknown", score: 0 }
        ]
      }
    ]
  },

  // LOGGING AND MONITORING SECTION
  logging_and_monitoring: {
    section_id: "logging_and_monitoring",
    questions: [
      {
        question_id: "LOG-01",
        field_id: "centralised_logging",
        prompt: "Is centralised logging in place?",
        type: "status",
        weight: 4,
        criticality: "high",
        gating_rule: false,
        required: true,
        recommended_components: ["microsoft_sentinel"],
        recommended_actions: ["Implement centralised log collection and analysis"]
      },
      {
        question_id: "LOG-02",
        field_id: "logging_tool",
        prompt: "What logging/SIEM tool is in use?",
        type: "selectScored",
        weight: 3,
        criticality: "high",
        gating_rule: false,
        required: true,
        recommended_components: ["microsoft_sentinel"],
        options: [
          { value: "microsoft_sentinel", label: "Microsoft Sentinel", score: 2 },
          { value: "splunk", label: "Splunk", score: 2 },
          { value: "other_siem", label: "Other SIEM", score: 2 },
          { value: "native_logs_only", label: "Native Logs Only", score: 1 },
          { value: "none", label: "None", score: 0 },
          { value: "unknown", label: "Unknown", score: 0 }
        ]
      },
      {
        question_id: "LOG-03",
        field_id: "alerts_actively_monitored",
        prompt: "Are security alerts actively monitored?",
        type: "status",
        weight: 3,
        criticality: "high",
        gating_rule: false,
        required: true,
        recommended_components: ["microsoft_sentinel"],
        recommended_actions: ["Implement 24/7 monitoring or managed detection and response"]
      },
      {
        question_id: "LOG-04",
        field_id: "incident_response_process",
        prompt: "Is there a defined incident response process?",
        type: "status",
        weight: 3,
        criticality: "high",
        gating_rule: false,
        required: true,
        recommended_components: ["microsoft_sentinel", "remediation_tracker"],
        recommended_actions: ["Document and test incident response procedures"]
      }
    ]
  },

  // VULNERABILITY MANAGEMENT SECTION
  vulnerability_management: {
    section_id: "vulnerability_management",
    questions: [
      {
        question_id: "VUL-01",
        field_id: "regular_vulnerability_scans",
        prompt: "Are regular vulnerability scans performed?",
        type: "status",
        weight: 3,
        criticality: "high",
        gating_rule: false,
        required: true,
        recommended_components: ["tenable", "qualys"],
        recommended_actions: ["Implement scheduled vulnerability scanning"]
      },
      {
        question_id: "VUL-02",
        field_id: "vulnerability_tool",
        prompt: "What vulnerability scanning tool is in use?",
        type: "selectScored",
        weight: 2,
        criticality: "medium",
        gating_rule: false,
        required: true,
        recommended_components: ["tenable", "qualys"],
        options: [
          { value: "tenable", label: "Tenable", score: 2 },
          { value: "qualys", label: "Qualys", score: 2 },
          { value: "microsoft_defender", label: "Microsoft Defender", score: 2 },
          { value: "other", label: "Other", score: 1 },
          { value: "none", label: "None", score: 0 },
          { value: "unknown", label: "Unknown", score: 0 }
        ]
      },
      {
        question_id: "VUL-03",
        field_id: "vulnerabilities_tracked_and_remediated",
        prompt: "Are vulnerabilities tracked and remediated?",
        type: "status",
        weight: 3,
        criticality: "high",
        gating_rule: false,
        required: true,
        recommended_components: ["remediation_tracker"],
        recommended_actions: ["Implement vulnerability tracking and remediation workflow"]
      },
      {
        question_id: "VUL-04",
        field_id: "remediation_timelines_tracked",
        prompt: "Are remediation timelines tracked and enforced?",
        type: "status",
        weight: 2,
        criticality: "medium",
        gating_rule: false,
        required: true,
        recommended_components: ["remediation_tracker"],
        recommended_actions: ["Define and track remediation SLAs by severity"]
      }
    ]
  },

  // COMPLIANCE AND GOVERNANCE SECTION
  compliance_and_governance: {
    section_id: "compliance_and_governance",
    questions: [
      {
        question_id: "GOV-01",
        field_id: "frameworks_currently_tracked",
        prompt: "What compliance frameworks are currently tracked?",
        type: "multiselect",
        weight: 2,
        criticality: "medium",
        gating_rule: false,
        required: true,
        recommended_components: ["drata", "disp_dashboard"],
        options: [
          { value: "essential_eight_ml2", label: "Essential Eight (ML2 target)" },
          { value: "ism", label: "ISM (Information Security Manual)" },
          { value: "disp", label: "DISP (Defence Industry Security Program)" },
          { value: "iso_27001", label: "ISO 27001" },
          { value: "soc_2", label: "SOC 2" },
          { value: "nist_csf", label: "NIST CSF" },
          { value: "nist_800_171_cmmc", label: "NIST 800-171 / CMMC" },
          { value: "cis_controls", label: "CIS Critical Security Controls" },
          { value: "internal_policies_only", label: "Internal policies only (no formal framework)" },
          { value: "none", label: "None" },
          { value: "unknown", label: "Unknown" },
          { value: "other", label: "Other" }
        ]
      },
      {
        question_id: "GOV-02",
        field_id: "evidence_collection_method",
        prompt: "How is compliance evidence collected?",
        type: "selectScored",
        weight: 2,
        criticality: "medium",
        gating_rule: false,
        required: true,
        recommended_components: ["drata", "evidence_pack_generator"],
        options: [
          { value: "grc_platform", label: "GRC Platform", score: 2 },
          { value: "ticketing_system", label: "Ticketing System", score: 1 },
          { value: "spreadsheet_tracking", label: "Spreadsheet Tracking", score: 1 },
          { value: "manual_documents", label: "Manual Documents", score: 0 },
          { value: "none", label: "None", score: 0 },
          { value: "unknown", label: "Unknown", score: 0 }
        ]
      },
      {
        question_id: "GOV-03",
        field_id: "compliance_check_frequency",
        prompt: "How frequently are compliance checks performed?",
        type: "selectScored",
        weight: 2,
        criticality: "medium",
        gating_rule: false,
        required: true,
        recommended_components: ["drata"],
        options: [
          { value: "continuous", label: "Continuous", score: 2 },
          { value: "monthly", label: "Monthly", score: 2 },
          { value: "quarterly", label: "Quarterly", score: 1 },
          { value: "annually", label: "Annually", score: 1 },
          { value: "ad_hoc", label: "Ad-hoc", score: 0 },
          { value: "never", label: "Never", score: 0 },
          { value: "unknown", label: "Unknown", score: 0 }
        ]
      },
      {
        question_id: "GOV-04",
        field_id: "risk_register_maintained",
        prompt: "Is a risk register maintained?",
        type: "status",
        weight: 2,
        criticality: "medium",
        gating_rule: false,
        required: true,
        recommended_components: ["drata", "remediation_tracker"],
        recommended_actions: ["Implement and maintain a formal risk register"]
      },
      {
        question_id: "GOV-05",
        field_id: "compliance_management_approach",
        prompt: "How structured is your compliance management approach?",
        type: "selectScored",
        weight: 2,
        criticality: "medium",
        gating_rule: false,
        required: true,
        recommended_components: ["drata"],
        options: [
          { value: "integrated_grc", label: "Integrated GRC platform", score: 2 },
          { value: "structured_manual", label: "Structured but manual (e.g. SharePoint)", score: 2 },
          { value: "spreadsheet_based", label: "Spreadsheet-based tracking", score: 1 },
          { value: "ad_hoc_reactive", label: "Ad hoc / reactive", score: 0 },
          { value: "none", label: "None", score: 0 }
        ]
      },
      {
        question_id: "GOV-06",
        field_id: "disp_09_ict_accreditation",
        prompt: "Do you currently have any formally accredited ICT systems or environments?",
        type: "selectScored",
        weight: 2,
        criticality: "medium",
        gating_rule: false,
        required: true,
        options: [
          { value: "yes", label: "Yes", score: 2 },
          { value: "no", label: "No", score: 0 },
          { value: "unsure", label: "Unsure", score: 0 }
        ]
      },
      {
        question_id: "GOV-07",
        field_id: "asr_reporting",
        prompt: "Has the organisation established a formal process for the preparation and submission of the Annual Security Report (ASR) in line with DISP requirements?",
        type: "selectScored",
        weight: 3,
        criticality: "high",
        gating_rule: false,
        required: true,
        options: [
          { value: "fully_implemented", label: "Fully Implemented - Documented ASR process; responsibility assigned (e.g. CSO/SO); reporting inputs defined; example or draft ASR available", score: 2 },
          { value: "partially_implemented", label: "Partially Implemented - Awareness of ASR requirement; informal or incomplete process; no structured reporting mechanism", score: 1 },
          { value: "not_implemented", label: "Not Implemented - No ASR process defined; no ownership assigned", score: 0 }
        ]
      },
      {
        question_id: "GOV-08",
        field_id: "facility_scope_definition",
        prompt: "Has the organisation clearly defined and documented all facilities, sites, and environments that will be included within the DISP registration scope?",
        type: "selectScored",
        weight: 3,
        criticality: "high",
        gating_rule: false,
        required: true,
        options: [
          { value: "fully_defined", label: "Fully Defined - All facilities identified; scope formally documented; systems mapped to locations; security classification understood", score: 2 },
          { value: "partially_defined", label: "Partially Defined - Some facilities identified; scope incomplete or informal; no full mapping of systems", score: 1 },
          { value: "not_defined", label: "Not Defined - No clear definition of facilities; DISP scope unclear", score: 0 }
        ]
      }
    ]
  },

  // POLICY & GOVERNANCE SECTION
  policy_governance: {
    section_id: "policy_governance",
    questions: [
      {
        question_id: "POL-01",
        field_id: "pol_01",
        prompt: "Which of the following formal security policies are currently in place?",
        helperText: "Select the current state for each policy domain.",
        type: "multi_select_matrix",
        weight: 0.22,
        criticality: "high",
        gating_rule: false,
        required: true,
        tags: ["policy", "ml2", "governance"],
        domains: [
          "Information Security Policy",
          "Access Control Policy",
          "Patch / Vulnerability Management Policy",
          "Backup & Recovery Policy",
          "Incident Response Policy",
          "Logging & Monitoring Policy",
          "Acceptable Use Policy",
          "Endpoint / Device Security Policy",
          "Privileged Access Management Policy",
          "Third-Party / Supplier Security Policy",
          "Change Management Policy",
          "Security Awareness & Training Policy"
        ],
        states: [
          "Documented and approved",
          "Draft only",
          "Informal / not documented",
          "Not in place",
          "Unknown"
        ],
        scoring: {
          "Documented and approved": 100,
          "Draft only": 60,
          "Informal / not documented": 30,
          "Not in place": 0,
          "Unknown": 20
        }
      },
      {
        question_id: "POL-02",
        field_id: "pol_02",
        prompt: "Are security policies formally owned and approved by management?",
        type: "selectScored",
        weight: 0.12,
        criticality: "medium",
        gating_rule: false,
        required: true,
        options: [
          { value: "yes_formally_approved", label: "Yes - formally approved and version controlled", score: 2 },
          { value: "partially_approved", label: "Partially - some policies approved", score: 1 },
          { value: "no_formal_approval", label: "No formal approval process", score: 0 },
          { value: "unknown", label: "Unknown", score: 0 }
        ]
      },
      {
        question_id: "POL-03",
        field_id: "pol_03",
        prompt: "How often are security policies reviewed and updated?",
        type: "selectScored",
        weight: 0.10,
        criticality: "medium",
        gating_rule: false,
        required: true,
        options: [
          { value: "at_least_annually", label: "At least annually", score: 2 },
          { value: "every_2_3_years", label: "Every 2-3 years", score: 1 },
          { value: "ad_hoc", label: "Ad hoc", score: 1 },
          { value: "never_reviewed", label: "Never reviewed", score: 0 },
          { value: "unknown", label: "Unknown", score: 0 }
        ]
      },
      {
        question_id: "POL-04",
        field_id: "pol_04",
        prompt: "To what extent are policies enforced through technical controls?",
        type: "selectScored",
        weight: 0.24,
        criticality: "high",
        gating_rule: false,
        required: true,
        tags: ["enforcement", "ml2", "audit-critical"],
        options: [
          { value: "fully_enforced", label: "Fully enforced through technical controls", score: 2 },
          { value: "partially_enforced", label: "Partially enforced", score: 1 },
          { value: "mostly_manual", label: "Mostly manual / procedural", score: 1 },
          { value: "not_enforced", label: "Not enforced", score: 0 },
          { value: "unknown", label: "Unknown", score: 0 }
        ]
      },
      {
        question_id: "POL-05",
        field_id: "pol_05",
        prompt: "Can evidence be provided to demonstrate policy compliance?",
        type: "selectScored",
        weight: 0.16,
        criticality: "high",
        gating_rule: false,
        required: true,
        tags: ["evidence", "audit-critical"],
        options: [
          { value: "yes_centralised", label: "Yes - centralised and audit-ready", score: 2 },
          { value: "yes_scattered", label: "Yes - but scattered/manual", score: 1 },
          { value: "limited_evidence", label: "Limited evidence", score: 1 },
          { value: "no_evidence", label: "No evidence available", score: 0 },
          { value: "unknown", label: "Unknown", score: 0 }
        ]
      },
      {
        question_id: "POL-06",
        field_id: "pol_06",
        prompt: "Are staff trained on and required to acknowledge key security policies?",
        type: "selectScored",
        weight: 0.08,
        criticality: "medium",
        gating_rule: false,
        required: true,
        options: [
          { value: "yes_tracked", label: "Yes - training and acknowledgement are tracked", score: 2 },
          { value: "training_only", label: "Training only", score: 1 },
          { value: "informal_awareness", label: "Informal awareness only", score: 1 },
          { value: "no_formal_program", label: "No formal awareness program", score: 0 },
          { value: "unknown", label: "Unknown", score: 0 }
        ]
      },
      {
        question_id: "POL-07",
        field_id: "pol_07",
        prompt: "Do your security policies explicitly align to Essential Eight ML2 requirements?",
        type: "selectScored",
        weight: 0.08,
        criticality: "medium",
        gating_rule: false,
        required: true,
        tags: ["ml2", "alignment"],
        options: [
          { value: "fully_aligned", label: "Fully aligned and mapped", score: 2 },
          { value: "partially_aligned", label: "Partially aligned", score: 1 },
          { value: "indirect_alignment", label: "Indirect alignment only", score: 1 },
          { value: "no_alignment", label: "No alignment", score: 0 },
          { value: "unknown", label: "Unknown", score: 0 }
        ]
      },
      {
        question_id: "POL-08",
        field_id: "disp_10_policy_documentation",
        prompt: "Do you have documented security policies and procedures?",
        type: "selectScored",
        weight: 0.08,
        criticality: "medium",
        gating_rule: false,
        required: true,
        options: [
          { value: "yes", label: "Yes", score: 2 },
          { value: "partial", label: "Partial", score: 1 },
          { value: "no", label: "No", score: 0 }
        ]
      },
      {
        question_id: "POL-09",
        field_id: "disp_11_evidence_readiness",
        prompt: "Do you maintain evidence (e.g. screenshots, logs, reports) to support your security controls?",
        type: "selectScored",
        weight: 0.08,
        criticality: "medium",
        gating_rule: false,
        required: true,
        options: [
          { value: "yes", label: "Yes", score: 2 },
          { value: "partial", label: "Partial", score: 1 },
          { value: "no", label: "No", score: 0 }
        ]
      }
    ]
  },

  // CONTRACTS, CLASSIFICATION & DISP SCOPE SECTION
  disp_scope: {
    section_id: "disp_scope",
    questions: [
      {
        question_id: "DISP-01",
        field_id: "disp_01_defence_contract",
        prompt: "Does your organisation currently hold or expect to hold Defence-related contracts?",
        type: "selectScored",
        weight: 2,
        criticality: "medium",
        gating_rule: false,
        required: true,
        options: [
          { value: "yes", label: "Yes", score: 2 },
          { value: "no", label: "No", score: 0 },
          { value: "unsure", label: "Unsure", score: 0 }
        ]
      },
      {
        question_id: "DISP-02",
        field_id: "disp_02_contract_requires_disp",
        prompt: "Do any of these contracts require DISP membership?",
        type: "selectScored",
        weight: 2,
        criticality: "medium",
        gating_rule: false,
        required: true,
        options: [
          { value: "yes", label: "Yes", score: 2 },
          { value: "no", label: "No", score: 0 },
          { value: "unsure", label: "Unsure", score: 0 }
        ]
      },
      {
        question_id: "DISP-03",
        field_id: "disp_03_classification_level",
        prompt: "What is the highest level of information you expect to handle?",
        type: "selectScored",
        weight: 2,
        criticality: "medium",
        gating_rule: false,
        required: true,
        options: [
          { value: "official", label: "OFFICIAL", score: 2 },
          { value: "official_sensitive", label: "OFFICIAL: Sensitive", score: 2 },
          { value: "protected", label: "PROTECTED", score: 1 },
          { value: "secret", label: "SECRET", score: 0 },
          { value: "top_secret", label: "TOP SECRET", score: 0 },
          { value: "unknown", label: "Unknown", score: 0 }
        ]
      },
      {
        question_id: "DISP-12",
        field_id: "disp_12_contract_scope_type",
        prompt: "What type of work does your organisation perform for Defence or related clients?",
        type: "multiselect",
        weight: 2,
        criticality: "low",
        gating_rule: false,
        required: true,
        options: [
          { value: "consulting", label: "Consulting" },
          { value: "software", label: "Software Development" },
          { value: "infrastructure", label: "Infrastructure" },
          { value: "facility_access", label: "Facility Access" },
          { value: "classified_handling", label: "Classified Handling" },
          { value: "unknown", label: "Unknown" }
        ]
      }
    ]
  },

  // PHYSICAL SECURITY & FACILITIES SECTION
  physical_security: {
    section_id: "physical_security",
    questions: [
      {
        question_id: "DISP-06",
        field_id: "disp_06_physical_facility",
        prompt: "Does your organisation operate from a physical office or facility?",
        type: "selectScored",
        weight: 2,
        criticality: "medium",
        gating_rule: false,
        required: true,
        options: [
          { value: "yes", label: "Yes", score: 2 },
          { value: "no", label: "No", score: 0 },
          { value: "remote_only", label: "Remote only", score: 1 }
        ]
      },
      {
        question_id: "DISP-07",
        field_id: "disp_07_facility_access_control",
        prompt: "Is access to your facility controlled (e.g. locks, swipe cards, restricted entry)?",
        type: "selectScored",
        weight: 3,
        criticality: "high",
        gating_rule: false,
        required: true,
        options: [
          { value: "yes", label: "Yes", score: 2 },
          { value: "partial", label: "Partial", score: 1 },
          { value: "no", label: "No", score: 0 },
          { value: "unsure", label: "Unsure", score: 0 }
        ]
      },
      {
        question_id: "DISP-13",
        field_id: "disp_13_multiple_locations",
        prompt: "Does your organisation operate across multiple physical locations?",
        type: "selectScored",
        weight: 1,
        criticality: "low",
        gating_rule: false,
        required: true,
        options: [
          { value: "yes", label: "Yes", score: 1 },
          { value: "no", label: "No", score: 2 },
          { value: "unsure", label: "Unsure", score: 0 }
        ]
      }
    ]
  },

  // PERSONNEL SECURITY & CLEARANCES SECTION
  personnel_security: {
    section_id: "personnel_security",
    questions: [
      {
        question_id: "PER-01",
        field_id: "pre_employment_screening",
        prompt: "Does the organisation conduct pre-employment screening of personnel in accordance with recognised standards (e.g. AS4811), including identity verification, background checks, and employment history validation?",
        type: "selectScored",
        weight: 4,
        criticality: "critical",
        gating_rule: true,
        required: true,
        options: [
          { value: "fully_aligned", label: "Yes - screening is formalised and aligned to recognised standards (e.g. AS4811)", score: 2 },
          { value: "partial", label: "Partially - some screening checks are performed but not consistently or not aligned to a formal standard", score: 1 },
          { value: "basic", label: "Basic checks only (e.g. references or limited verification)", score: 1 },
          { value: "none", label: "No formal pre-employment screening is conducted", score: 0 },
          { value: "unknown", label: "Unknown", score: 0 }
        ]
      },
      {
        question_id: "PER-02",
        field_id: "baseline_vetting_required",
        prompt: "Are all personnel (including employees and contractors) who access Defence-related systems, information, or facilities subject to appropriate baseline vetting requirements?",
        type: "status",
        weight: 4,
        criticality: "critical",
        gating_rule: true,
        required: true,
        options: null
      },
      {
        question_id: "PER-03",
        field_id: "security_clearance_management",
        prompt: "Does the organisation have the capability to sponsor, manage, and maintain personnel security clearances (e.g. Baseline, NV1, NV2, PV) where required for Defence-related work?",
        type: "selectScored",
        weight: 3,
        criticality: "high",
        gating_rule: false,
        required: true,
        options: [
          { value: "formal_program", label: "Yes - formal clearance sponsorship and management processes are established", score: 2 },
          { value: "ad_hoc", label: "Partially - clearances are managed on an ad-hoc or case-by-case basis", score: 1 },
          { value: "outsourced", label: "Managed via external partner or prime contractor", score: 1 },
          { value: "none", label: "No capability to sponsor or manage security clearances", score: 0 },
          { value: "unknown", label: "Unknown", score: 0 }
        ]
      },
      {
        question_id: "PER-04",
        field_id: "clearance_tracking",
        prompt: "Are personnel security clearances recorded, tracked, and actively monitored to ensure validity and timely renewal prior to expiry?",
        type: "status",
        weight: 3,
        criticality: "high",
        gating_rule: false,
        required: true,
        options: null
      },
      {
        question_id: "PER-05",
        field_id: "insider_threat_training",
        prompt: "Does the organisation provide personnel with security awareness and insider threat training, including guidance on recognising and reporting suspicious or malicious behaviour?",
        type: "selectScored",
        weight: 3,
        criticality: "high",
        gating_rule: false,
        required: true,
        options: [
          { value: "formal_program", label: "Yes - formal, ongoing training program with participation tracking", score: 2 },
          { value: "periodic_training", label: "Periodic or annual training is provided", score: 1 },
          { value: "informal", label: "Informal awareness activities only", score: 1 },
          { value: "none", label: "No formal security awareness or insider threat training", score: 0 },
          { value: "unknown", label: "Unknown", score: 0 }
        ]
      },
      {
        question_id: "PER-06",
        field_id: "offboarding_controls",
        prompt: "Are formal personnel offboarding procedures implemented to ensure timely revocation of system access, removal of permissions, and return of organisational assets when personnel cease engagement?",
        type: "status",
        weight: 4,
        criticality: "critical",
        gating_rule: true,
        required: true,
        options: null
      },
      {
        question_id: "PER-07",
        field_id: "contractor_security_controls",
        prompt: "Are contractors, subcontractors, and third-party personnel subject to equivalent personnel security, vetting, and access control requirements as direct employees?",
        type: "selectScored",
        weight: 3,
        criticality: "high",
        gating_rule: false,
        required: true,
        options: [
          { value: "fully_aligned", label: "Yes - equivalent controls are enforced consistently", score: 2 },
          { value: "partial", label: "Partially - some controls are applied but not consistently", score: 1 },
          { value: "minimal", label: "Minimal controls applied to third parties", score: 1 },
          { value: "none", label: "No consistent personnel security controls for third parties", score: 0 },
          { value: "unknown", label: "Unknown", score: 0 }
        ]
      }
    ]
  },

  // FOREIGN OWNERSHIP, CONTROL & INFLUENCE (FOCI) SECTION
  foci: {
    section_id: "foci",
    questions: [
      {
        question_id: "FOCI-01",
        field_id: "foci_01_foreign_ownership",
        prompt: "Does your organisation have any foreign ownership or shareholding?",
        type: "selectScored",
        weight: 2,
        criticality: "high",
        gating_rule: false,
        required: true,
        options: [
          { value: "yes", label: "Yes", score: 1 },
          { value: "no", label: "No", score: 2 },
          { value: "unsure", label: "Unsure", score: 0 }
        ]
      },
      {
        question_id: "FOCI-02",
        field_id: "foci_02_foreign_influence",
        prompt: "Are any board members, executives, or key decision-makers based outside Australia?",
        type: "selectScored",
        weight: 2,
        criticality: "high",
        gating_rule: false,
        required: true,
        options: [
          { value: "yes", label: "Yes", score: 1 },
          { value: "no", label: "No", score: 2 },
          { value: "unsure", label: "Unsure", score: 0 }
        ]
      },
      {
        question_id: "FOCI-03",
        field_id: "foci_03_foreign_entities_20",
        prompt: "Is there any foreign entity or related group with 20% or more ownership?",
        type: "selectScored",
        weight: 3,
        criticality: "critical",
        gating_rule: false,
        required: true,
        options: [
          { value: "yes", label: "Yes", score: 1 },
          { value: "no", label: "No", score: 2 },
          { value: "unsure", label: "Unsure", score: 0 }
        ]
      },
      {
        question_id: "FOCI-04",
        field_id: "foci_04_foreign_entities_40",
        prompt: "Are there two or more foreign entities with combined ownership of 40% or more?",
        type: "selectScored",
        weight: 3,
        criticality: "critical",
        gating_rule: false,
        required: true,
        options: [
          { value: "yes", label: "Yes", score: 1 },
          { value: "no", label: "No", score: 2 },
          { value: "unsure", label: "Unsure", score: 0 }
        ]
      },
      {
        question_id: "FOCI-05",
        field_id: "foci_05_foreign_control",
        prompt: "Can any foreign person or organisation influence or control business decisions?",
        type: "selectScored",
        weight: 3,
        criticality: "critical",
        gating_rule: false,
        required: true,
        options: [
          { value: "yes", label: "Yes", score: 1 },
          { value: "no", label: "No", score: 2 },
          { value: "unsure", label: "Unsure", score: 0 }
        ]
      },
      {
        question_id: "FOCI-06",
        field_id: "foci_06_foreign_arrangements",
        prompt: "Does your organisation have any significant foreign agreements, obligations, or partnerships?",
        type: "selectScored",
        weight: 2,
        criticality: "medium",
        gating_rule: false,
        required: true,
        options: [
          { value: "yes", label: "Yes", score: 1 },
          { value: "no", label: "No", score: 2 },
          { value: "unsure", label: "Unsure", score: 0 }
        ]
      },
      {
        question_id: "FOCI-07",
        field_id: "foci_07_complexity",
        prompt: "How would you describe your FOCI situation?",
        type: "selectScored",
        weight: 3,
        criticality: "high",
        gating_rule: false,
        required: true,
        options: [
          { value: "simple", label: "Simple - no foreign involvement or minimal and well-understood", score: 2 },
          { value: "moderate", label: "Moderate - some foreign elements but manageable", score: 1 },
          { value: "complex", label: "Complex - significant foreign involvement requiring detailed assessment", score: 0 },
          { value: "unsure", label: "Unsure", score: 0 }
        ]
      }
    ]
  },

  // PAIN POINTS SECTION (not scored)
  pain_points: {
    section_id: "pain_points",
    questions: [
      {
        question_id: "PAI-01",
        field_id: "primary_pain_drivers",
        prompt: "What are the organisation's primary cybersecurity and compliance challenges?",
        type: "multiselect",
        weight: 2,
        criticality: "medium",
        gating_rule: false,
        required: true,
        recommended_components: ["disp_dashboard", "drata", "remediation_tracker"],
        options: [
          { value: "lack_cyber_expertise", label: "Lack of internal cybersecurity expertise" },
          { value: "difficulty_e8_ml2", label: "Difficulty achieving Essential Eight ML2" },
          { value: "no_centralised_visibility", label: "No centralised visibility of security posture" },
          { value: "inconsistent_patching", label: "Inconsistent patching and vulnerability management" },
          { value: "too_many_manual_processes", label: "Too many manual processes" },
          { value: "lack_documentation", label: "Lack of documentation / policies" },
          { value: "struggling_audit_requirements", label: "Struggling with audit requirements" },
          { value: "difficulty_collecting_evidence", label: "Difficulty collecting evidence" },
          { value: "lack_logging_monitoring", label: "Lack of logging and monitoring capability" },
          { value: "poor_identity_access_control", label: "Poor identity and access control" },
          { value: "too_many_privileged_users", label: "Too many privileged users / admin rights" },
          { value: "backup_recovery_concerns", label: "Backup and recovery concerns" },
          { value: "unclear_disp_roadmap", label: "Unclear roadmap to DISP compliance" },
          { value: "time_resource_constraints", label: "Time constraints / resource limitations" },
          { value: "budget_constraints", label: "Budget constraints" },
          { value: "other", label: "Other" }
        ]
      },
      {
        question_id: "PAI-02",
        field_id: "compliance_pressure",
        prompt: "What level of pressure is the organisation under to achieve DISP / ML2 compliance?",
        type: "selectScored",
        weight: 2,
        criticality: "high",
        gating_rule: false,
        required: true,
        recommended_components: ["disp_dashboard", "remediation_tracker"],
        options: [
          { value: "immediate", label: "Immediate (active contract or audit pending)", score: 2 },
          { value: "high", label: "High (bidding or contract expected soon)", score: 2 },
          { value: "medium", label: "Medium (planned within 6–12 months)", score: 1 },
          { value: "low", label: "Low (exploring / future consideration)", score: 1 },
          { value: "unknown", label: "Unknown", score: 0 }
        ]
      },
      {
        question_id: "PAI-03",
        field_id: "ml2_progress",
        prompt: "What progress has been made towards Essential Eight ML2?",
        type: "selectScored",
        weight: 2,
        criticality: "high",
        gating_rule: false,
        required: true,
        recommended_components: ["disp_dashboard", "drata"],
        options: [
          { value: "fully_aligned", label: "Fully aligned to ML2", score: 2 },
          { value: "substantially_aligned", label: "Substantially aligned", score: 2 },
          { value: "partial_implementation", label: "Partial implementation", score: 1 },
          { value: "minimal_implementation", label: "Minimal implementation", score: 0 },
          { value: "no_implementation", label: "No implementation", score: 0 },
          { value: "unknown", label: "Unknown", score: 0 }
        ]
      },
      {
        question_id: "PAI-04",
        field_id: "audit_evidence_capability",
        prompt: "How would you describe your current audit and evidence management capability?",
        type: "selectScored",
        weight: 2,
        criticality: "medium",
        gating_rule: false,
        required: true,
        recommended_components: ["evidence_pack_generator", "drata"],
        options: [
          { value: "fully_automated", label: "Fully automated and structured", score: 2 },
          { value: "mostly_structured", label: "Mostly structured with some manual work", score: 2 },
          { value: "largely_manual", label: "Largely manual and time-consuming", score: 1 },
          { value: "no_formal_process", label: "No formal process", score: 0 },
          { value: "unknown", label: "Unknown", score: 0 }
        ]
      },
      {
        question_id: "PAI-05",
        field_id: "engagement_driver",
        prompt: "What is the primary driver for this engagement?",
        type: "select",
        weight: 1,
        criticality: "low",
        gating_rule: false,
        required: true,
        notesPlaceholder: "Additional context or notes on challenges and drivers (optional)",
        recommended_components: ["disp_dashboard"],
        options: [
          { value: "defence_contract_requirement", label: "Defence contract requirement" },
          { value: "disp_application", label: "DISP application" },
          { value: "audit_compliance_requirement", label: "Audit / compliance requirement" },
          { value: "risk_reduction", label: "Risk reduction" },
          { value: "customer_requirement", label: "Customer requirement (Prime or partner)" },
          { value: "internal_initiative", label: "Internal initiative" },
          { value: "incident_security_concern", label: "Incident or security concern" },
          { value: "other", label: "Other" }
        ]
      }
    ]
  },

  // ==========================================================================
  // EVIDENCE READINESS (informational-only; excluded from ML2/CES/PGS/CMS scoring)
  // See lib/evidence-readiness.js for the independent scoring pipeline.
  // Field IDs MUST match evidenceGroupFieldId(group.id) in evidence-readiness.js.
  // ==========================================================================
  evidence_readiness: {
    section_id: "evidence_readiness",
    title: "Evidence Readiness",
    description: "Identify the evidence currently available within your organisation. At this stage, DefencIQ Discovery only confirms whether major categories of supporting evidence exist. Evidence will be collected and validated during the Accelerator phase.",
    questions: (() => {
      const EV = [
        { id: "GOV-EV-01", name: "Security Policies", tip: "Examples include information security policy, acceptable use policy, access control policy, and cryptographic policy." },
        { id: "GOV-EV-02", name: "Risk Management Documentation", tip: "Examples include risk register, risk assessments, treatment plans, and residual risk acceptance records." },
        { id: "GOV-EV-03", name: "Compliance & Assurance Documentation", tip: "Examples include audit reports, compliance attestations, control matrices, and third-party assurance reviews." },
        { id: "GOV-EV-04", name: "DISP Registration Scope Documentation", tip: "Examples include DISP membership records, scope statements, facility lists, and personnel scope definitions." },
        { id: "GOV-EV-05", name: "Annual Security Report (ASR) Documentation", tip: "Examples include prior ASR submissions, evidence packs, and Defence correspondence relating to ASR." },
        { id: "GOV-EV-06", name: "Ownership & Control Documentation", tip: "Examples include beneficial ownership records, shareholder registers, board composition, and control disclosures." },
        { id: "GOV-EV-07", name: "FOCI Assessment & Mitigation Documentation", tip: "Examples include FOCI disclosures, mitigation plans, foreign influence assessments, and Defence responses." },
        { id: "PER-EV-01", name: "Personnel Screening Records", tip: "Examples include pre-employment checks, identity verification, reference checks, and screening decisions." },
        { id: "PER-EV-02", name: "Baseline Vetting Records", tip: "Examples include AGSVA baseline records, sponsorship documents, and personnel security file references." },
        { id: "PER-EV-03", name: "Security Clearance Management Records", tip: "Examples include clearance registers, renewal tracking, briefings/debriefings, and access-authorisation records." },
        { id: "PER-EV-04", name: "Security Awareness & Insider Threat Records", tip: "Examples include training completion records, awareness campaign artefacts, and insider-threat program documentation." },
        { id: "PER-EV-05", name: "Personnel Offboarding Records", tip: "Examples include exit checklists, access revocation confirmations, asset returns, and clearance debrief records." },
        { id: "PER-EV-06", name: "Contractor & Third-Party Security Records", tip: "Examples include contractor screening evidence, third-party agreements, NDAs, and sub-contractor DISP status." },
        { id: "PHY-EV-01", name: "Physical Access Control Documentation", tip: "Examples include zone plans, access-control policies, badge issuance records, and access audit logs." },
        { id: "PHY-EV-02", name: "Visitor Management Documentation", tip: "Examples include visitor logs, escort policies, sign-in system records, and visitor briefings." },
        { id: "PHY-EV-03", name: "Facility Security Documentation", tip: "Examples include facility security plans, T4/Zone certifications, and secure area authorisation records." },
        { id: "PHY-EV-04", name: "Physical Monitoring & Alarm Documentation", tip: "Examples include CCTV coverage plans, alarm system records, monitoring rosters, and incident response records." },
        { id: "ICT-EV-01", name: "Identity & MFA Evidence", tip: "Examples include MFA policies, identity platform configuration, conditional access policies, authentication logs and MFA coverage reports." },
        { id: "ICT-EV-02", name: "Privileged Access Management Evidence", tip: "Examples include PAM tool configuration, privileged session recordings, JIT elevation logs, and privileged-account inventory." },
        { id: "ICT-EV-03", name: "Endpoint Management Evidence", tip: "Examples include MDM/UEM configuration, device compliance reports, endpoint enrolment records, and baseline configurations." },
        { id: "ICT-EV-04", name: "Device Security & Encryption Evidence", tip: "Examples include disk encryption reports, EDR coverage exports, secure-configuration baselines, and device-hardening records." },
        { id: "ICT-EV-05", name: "Application Control Evidence", tip: "Examples include allow-listing policies, execution-control tool configuration, unauthorised-execution logs, and exception registers." },
        { id: "ICT-EV-06", name: "Application Hardening Evidence", tip: "Examples include browser and Office hardening policies, script/macro control configuration, and PDF/HTML restriction settings." },
        { id: "ICT-EV-07", name: "Patch Management Evidence", tip: "Examples include patch policies, patch compliance reports, remediation SLAs, and unpatched-system exception records." },
        { id: "ICT-EV-08", name: "Vulnerability Management Evidence", tip: "Examples include vulnerability scanner outputs, remediation tickets, CVSS-based prioritisation records, and re-scan verifications." },
        { id: "ICT-EV-09", name: "Security Monitoring & Logging Evidence", tip: "Examples include SIEM configuration, log retention policies, detection rule inventory, and alert triage records." },
        { id: "ICT-EV-10", name: "Incident Response Evidence", tip: "Examples include IR plans, playbooks, post-incident reviews, tabletop exercise records, and IR contact lists." },
        { id: "ICT-EV-11", name: "Backup & Recovery Evidence", tip: "Examples include backup policies, restoration test logs, immutability configuration, and RPO/RTO validation records." },
      ];
      return EV.map(({ id, name, tip }) => ({
        question_id: id,
        field_id: id.toLowerCase().replace(/-/g, "_"),
        prompt: name,
        help_text: tip,
        type: "selectScored",
        weight: 0,           // MUST remain 0 - never scored in ML2/CES/PGS/CMS
        criticality: null,
        gating_rule: false,
        required: false,
        options: [
          { value: "available", label: "Available", score: 2 },
          { value: "partially_available", label: "Partially Available", score: 1 },
          { value: "not_available", label: "Not Available", score: 0 },
        ]
      }));
    })()
  }
};

// Get all questions as a flat array
export const getAllQuestions = () => {
  const questions = [];
  Object.values(QUESTION_CONFIG).forEach(section => {
    section.questions.forEach(q => {
      questions.push({ ...q, section_id: section.section_id });
    });
  });
  return questions;
};

// Get questions by section
export const getQuestionsBySection = (sectionId) => {
  return QUESTION_CONFIG[sectionId]?.questions || [];
};

// Get question by field_id
export const getQuestionByFieldId = (fieldId) => {
  for (const section of Object.values(QUESTION_CONFIG)) {
    const question = section.questions.find(q => q.field_id === fieldId);
    if (question) return { ...question, section_id: section.section_id };
  }
  return null;
};

// Get all gating questions
export const getGatingQuestions = () => {
  return getAllQuestions().filter(q => q.gating_rule === true);
};

// Get all critical questions
export const getCriticalQuestions = () => {
  return getAllQuestions().filter(q => q.criticality === 'critical');
};

// Section order for navigation
export const SECTION_ORDER = [
  'engagement',
  'organisation_profile',
  'current_tooling',
  'identity_and_mfa',
  'endpoint_management',
  'application_control',
  'patch_management',
  'privileged_access',
  'office_macros_and_user_hardening',
  'backups',
  'logging_and_monitoring',
  'vulnerability_management',
  'policy_governance',
  'compliance_and_governance',
  'disp_scope',
  'physical_security',
  'personnel_security',
  'foci',
  'evidence_readiness',
  'pain_points'
];

// Scored sections (for domain calculation)
// NOTE: evidence_readiness is EXCLUDED - it is an independent informational signal
// computed separately in lib/evidence-readiness.js and must not affect ML2/CES/PGS/CMS.
export const SCORED_SECTIONS = SECTION_ORDER.filter(
  s => s !== 'engagement' && s !== 'pain_points' && s !== 'current_tooling' && s !== 'evidence_readiness'
);

export default QUESTION_CONFIG;
